import React, { useState, useCallback, useEffect, useContext, useRef } from 'react';
import t from 'react-translate';
import { Button } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { AngularServicesContext } from 'react-app';
import humps from 'humps';

// styles
import { Global, css } from '@emotion/react';
import { black, gray5, gray7, primary } from 'styles/global_defaults/colors';

// redux
import { RootState } from 'redux/schemas';
import { getCourseAliases, getCurrentCourse } from 'redux/selectors/course';
import { useDispatch, useSelector } from 'react-redux';
import { CourseAliases } from 'redux/schemas/models/course';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { bulkConvertToCollectionLessons } from 'redux/actions/lecture-pages';
import { fetchBulkConvertibleLecturePages, setBulkConvertInProgress } from 'redux/actions/collections';

// components
import NvIcon from 'shared/components/nv-icon';
import { NvModal, ModalType } from 'shared/components/nv-modal';
import NvTooltip from 'shared/components/nv-tooltip';
import ClickableContainer from 'components/clickable-container';
import { getScopeType } from 'course_home/components/header/edit-menu/check-bulk-convertible-lecture-pages-eligibility';
import pusherService from 'shared/services/pusher-service';
import { BulkConvertCompletedEvent, SyncingStatus } from 'redux/schemas/models/lecture-page';
import CollectionListModalBody, { CollectionListModalType, ModalActionType } from './collection-list-modal-body';
import CreateCollectionAndAddToCollection from './create-collection-and-add-to-collection';

export enum BulkAddToCollectionSourceType {
  COURSE = 'Course',
  SECTION = 'Section',
  SUBSECTION = 'Subsection',
}

export type BulkAddToCollectionModalProps = {
  show: boolean;
  onClose: () => void;
  sourceType: BulkAddToCollectionSourceType;
  sourceId: number;
  sourceName?: string;
  pageCount?: number;
};

const styles = css`
  .overflow-clip {
    overflow: clip !important;
  }

  .collection-list-modal-body {
    margin: 0px !important;

    .content {
      max-height: calc(100vh - 600px);
    }
  }

  .folder-info-bar {
    border: 1px solid ${gray5}
  }
`;

const alertMessageExtraStyle = css`
  .flash-close {
    .icon-close {
      color: ${black} !important;
      font-size: 16px;
    }
  }

  .flash-button {
    width: 30%;
    display: flex;
    justify-content: end !important;
  }

  &.flash-info {
    border-top-color: ${primary} !important;

    .flash-icon {
      color: ${primary} !important;
    }
  }

  .bs4-btn-link {
    &:focus, &:active, &:hover {
      text-decoration: none;
      box-shadow: none !important;
    }
  }
`;

const SourceDetails = ({ sourceType, sourceName, pageCount }) => {
  const aliases: CourseAliases = useSelector((state: RootState) => getCourseAliases(state));

  return (
    <React.Fragment>
      <div className='page-title-small font-weight-bolder'>{t.TIMELINE.LL_BULK_CONVERT_LECTURE_PAGES.LL_BULK_CONVERT_LECTURE_PAGES_MODAL.TITLE(sourceType)}</div>
      <div className='text-large-body'>{t.TIMELINE.LL_BULK_CONVERT_LECTURE_PAGES.LL_BULK_CONVERT_LECTURE_PAGES_MODAL.SUB_TITLE(aliases.lectureAliases)}</div>
      <React.Fragment>
        <div className='d-flex text-small justify-content-between px-4 py-2 bg-gray-7 my-4 folder-info-bar'>
          <div>
            <div className='mb-1 text-gray-2'>{t.TIMELINE.LL_BULK_CONVERT_LECTURE_PAGES.LL_BULK_CONVERT_LECTURE_PAGES_MODAL.SOURCE_DETAILS.SOURCE_TYPE_NAME(sourceType)}</div>
            <div className='text-black font-weight-bold'>{sourceName}</div>
          </div>
          <div>
            <div className='mb-1 text-gray-2'>{t.TIMELINE.LL_BULK_CONVERT_LECTURE_PAGES.LL_BULK_CONVERT_LECTURE_PAGES_MODAL.SOURCE_DETAILS.LESSONS_TO_BE_ADDED(aliases.lectureAliases)}</div>
            <div className='d-flex align-items-center'>
              <div className='text-black font-weight-bold'>{t.TIMELINE.LL_BULK_CONVERT_LECTURE_PAGES.LL_BULK_CONVERT_LECTURE_PAGES_MODAL.SOURCE_DETAILS.SOURCE_LESSONS_COUNT({ lessonPagesCount: pageCount, lectureAlias: aliases.lectureAliases.LecturesAlias })}</div>
              <NvTooltip
                placement='top'
                maxWidth={250}
                text={t.TIMELINE.LL_BULK_CONVERT_LECTURE_PAGES.LL_BULK_CONVERT_LECTURE_PAGES_MODAL.SOURCE_DETAILS.SOURCE_LESSONS_COUNT_TOOLTIP_TEXT(aliases.lectureAliases)}
              >
                <div className='ml-1 text-primary'>
                  <NvIcon icon='info' size='xss-smallest' />
                </div>
              </NvTooltip>
            </div>
          </div>
        </div>
      </React.Fragment>
    </React.Fragment>
  );
};

const BulkAddToCollectionModal = (props: BulkAddToCollectionModalProps) => {
  const {
    show,
    onClose,
    sourceType,
    sourceId,
    sourceName,
    pageCount = 0,
  } = props;
  const aliases: CourseAliases = useSelector((state: RootState) => getCourseAliases(state));

  const methods = useForm({
    mode: 'all',
    defaultValues: {
      destinationCollectionName: null,
      destinationCollectionId: null,
      destinationCatalogId: null,
    },
  });

  const { reset, getValues } = methods;
  const dispatch = useDispatch();
  const { $state, TimelinesManager, $scope, CurrentCourseManager, CurrentPermissionsManager, $rootScope } = useContext(AngularServicesContext);
  const [mode, setMode] = useState<'select' | 'create'>('select');
  const currentCourse = useSelector(getCurrentCourse);
  const scopeType = getScopeType(sourceType);
  const bulkConvertCommandsRef = useRef<
  Record<number, {
    scopeType: string
    scopeId: number
    lecturePageIds: number[]
  }>
  >({});
  const canCreateNewCollection = CurrentPermissionsManager?.hasCourseManagerPermissions();

  const setLecturePagesSyncingStatus = useCallback((lecturePageIds: number[], status: SyncingStatus) => {
    if (!TimelinesManager || !lecturePageIds?.length) return;

    lecturePageIds.forEach((lecturePageId) => {
      if (status === SyncingStatus.LINK_IN_PROGRESS) {
        TimelinesManager.updateLecturePageAttribute(lecturePageId, 'isLinked', true);
      }
      TimelinesManager.updateLecturePageAttribute(lecturePageId, 'syncingStatus', status);
    });
    $scope?.$applyAsync?.();
  }, [TimelinesManager, $scope]);

  const applyBulkConvertSyncingStatus = useCallback((lecturePageIds: number[]) => {
    if (!lecturePageIds?.length) return;

    setLecturePagesSyncingStatus(lecturePageIds, SyncingStatus.LINK_IN_PROGRESS);
  }, [setLecturePagesSyncingStatus]);

  const redirectToCollectionFolder = useCallback((stateParams) => {
    window.open($state?.href(
      'content-library-collection-home',
      stateParams,
    ), '_blank');
  }, [$state]);

  const setBulkConvertInProgressState = useCallback((value: boolean) => {
    dispatch(setBulkConvertInProgress(value));
    CurrentCourseManager.course.updateFromReact({ bulkConvertInProgress: value });
  }, [dispatch, CurrentCourseManager.course]);

  const dispatchBulkConvertInProgressIfCourse = useCallback((value: boolean) => {
    if (sourceType === BulkAddToCollectionSourceType.COURSE) {
      setBulkConvertInProgressState(value);
    }
  }, [sourceType, setBulkConvertInProgressState]);


  const applyScopeBulkConvertInProgress = useCallback((commandScopeType: string, commandScopeId: number, value: boolean) => {
    if (!TimelinesManager?.currentTimeline) return;
    const { lectureSections, lectureSubsections } = TimelinesManager.currentTimeline;

    if (commandScopeType === getScopeType(BulkAddToCollectionSourceType.SECTION)) {
      const sectionResult = lectureSections?.find((section) => section.id === commandScopeId);
      if (sectionResult) {
        sectionResult.bulkConvertInProgress = value;
        sectionResult.lectureSubsections?.forEach((subsection) => {
          subsection.bulkConvertInProgress = value;
        });
      }
    }

    if (commandScopeType === getScopeType(BulkAddToCollectionSourceType.SUBSECTION)) {
      const nestedSubsections = (lectureSections || []).flatMap((section) => section.lectureSubsections);
      const allSubsections = [...lectureSubsections, ...nestedSubsections];
      const subsection = allSubsections.find((s) => s.id === commandScopeId);
      if (subsection) {
        subsection.bulkConvertInProgress = value;
      }
    }
  }, [TimelinesManager]);

  const updateScopeBulkConvertInProgressState = useCallback(() => {
    if (!TimelinesManager?.currentTimeline) return;
    const { lectureSections, lectureSubsections } = TimelinesManager.currentTimeline;

    // Reset all scope-level flags first.
    (lectureSections || []).forEach((section) => {
      section.bulkConvertInProgress = false;
      (section.lectureSubsections || []).forEach((subsection) => {
        subsection.bulkConvertInProgress = false;
      });
    });

    (lectureSubsections || []).forEach((subsection) => {
      subsection.bulkConvertInProgress = false;
    });

    // Re-apply flags from every active command to support overlapping conversions.
    Object.values(bulkConvertCommandsRef.current).forEach((command) => {
      applyScopeBulkConvertInProgress(command.scopeType, command.scopeId, true);
    });

    $scope?.$applyAsync?.();
  }, [TimelinesManager, $scope, applyScopeBulkConvertInProgress]);

  useEffect(() => {
    if (!currentCourse?.id) return undefined;

    const pusherChannel = pusherService.initializeCourseChannel(currentCourse.id);
    if (!pusherChannel) return undefined;

    const onBulkConvertCompleted = (data) => {
      const pusherData = humps.camelizeKeys(data) as unknown as BulkConvertCompletedEvent;

      const currentBulkConvertCommand = bulkConvertCommandsRef.current[pusherData.id];

      // Ignore events not initiated by this modal instance (prevents duplicate handling across multiple listeners)
      if (!currentBulkConvertCommand) return;
      const pendingLecturePageIds = currentBulkConvertCommand.lecturePageIds;
      // Update SyncingStatus UI for only this command
      if (pendingLecturePageIds.length > 0) {
        setLecturePagesSyncingStatus(pendingLecturePageIds, SyncingStatus.COMPLETED);
      }

      // Clean up immediately (prevents duplicate handling)
      delete bulkConvertCommandsRef.current[pusherData.id];

      const { convertibleLecturePagesCount, convertedLecturePagesCount, contentManagementCollectionCatalogId } = pusherData;
      const collectionTitle = pusherData?.contentManagementCollectionTitle;
      const collectionId = pusherData?.contentManagementCollectionId;
      const hasValidCounts = Number.isFinite(convertibleLecturePagesCount)
        && Number.isFinite(convertedLecturePagesCount);
      const isError = !hasValidCounts || convertedLecturePagesCount === 0;

      let alertType: AlertMessageType;
      let header: string;
      let message: string;

      if (!hasValidCounts) {
        alertType = AlertMessageType.ERROR;
        header = t.FORM.OOPS();
        message = t.FORM.ERROR_SOMETHING_WRONG();
      } else if (convertedLecturePagesCount === 0) {
        alertType = AlertMessageType.ERROR;
        header = t.FORM.OOPS();
        message = t.FORM.ERROR_SOMETHING_WRONG_SHORT();
      } else if (convertedLecturePagesCount < convertibleLecturePagesCount) {
        alertType = AlertMessageType.INFO;
        header = t.TIMELINE.LL_BULK_CONVERT_LECTURE_PAGES.ALERT_MESSAGES.ALL_DONE();
        message = t.TIMELINE.LL_BULK_CONVERT_LECTURE_PAGES.ALERT_MESSAGES.SUCCESS_MESSAGE({ collectionName: collectionTitle, convertedCount: convertedLecturePagesCount, totalCount: convertibleLecturePagesCount, LecturesAlias: aliases.lectureAliases.LecturesAlias });
      } else {
        alertType = AlertMessageType.SUCCESS;
        header = t.FORM.SUCCESS_BANG();
        message = t.TIMELINE.LL_BULK_CONVERT_LECTURE_PAGES.ALERT_MESSAGES.SUCCESS_MESSAGE({ collectionName: collectionTitle, convertedCount: convertedLecturePagesCount, totalCount: convertibleLecturePagesCount, LecturesAlias: aliases.lectureAliases.LecturesAlias });
      }

      dispatch(addAlertMessage({
        type: alertType,
        header,
        message,
        extraStyles: alertMessageExtraStyle,
        button: isError ? undefined : (
          <Button
            variant='link'
            className='d-flex align-items-center'
            onClick={() => redirectToCollectionFolder(
              {
                collectionId,
                catalogId: contentManagementCollectionCatalogId,
              },
            )}
          >
            <NvIcon icon='open-external' size='xs-small' className='mr-1' />
            <div className='font-weight-bolder'>{t.INSTITUTIONS.CONTENT_LIBRARY.COLLECTIONS_HOME.GO_TO_COLLECTION()}</div>
          </Button>
        ),
      }));

      const isCourseConversion = currentBulkConvertCommand.scopeType === getScopeType(BulkAddToCollectionSourceType.COURSE)
        && currentBulkConvertCommand.scopeId === currentCourse.id;

      const updateBulkConvertProgressState = () => {
        if (isCourseConversion) {
          setBulkConvertInProgressState(false);
        } else {
          updateScopeBulkConvertInProgressState();
        }
      };

      dispatch(fetchBulkConvertibleLecturePages({ courseId: currentCourse.id }))
        .unwrap()
        .then(() => {
          $rootScope.$broadcast(
            'refresh-convertible-counts',
            { courseId: currentCourse.id },
          );
          updateBulkConvertProgressState();
        })
        .catch(() => {
          updateBulkConvertProgressState();
        });
    };

    pusherChannel.bind('content_management_bulk_convert_command_completed', onBulkConvertCompleted);
    return () => {
      pusherChannel.unbind('content_management_bulk_convert_command_completed', onBulkConvertCompleted);
    };
  }, [aliases.lectureAliases.LecturesAlias, currentCourse.id, dispatch, redirectToCollectionFolder, setBulkConvertInProgressState, setLecturePagesSyncingStatus, updateScopeBulkConvertInProgressState]);


  const handleClose = useCallback(() => {
    reset(); // resets to defaultValues
    setMode('select');
    onClose(); // parent close
  }, [reset, onClose]);


  const handleBulkConvertResponse = useCallback((result) => {
    if (!result.error && result.payload?.id) {
      const commandId = result.payload?.id;
      const convertibleLecturePageIds = result.payload?.convertibleLecturePageIds || [];
      bulkConvertCommandsRef.current[commandId] = {
        scopeType,
        scopeId: sourceId,
        lecturePageIds: convertibleLecturePageIds,
      };

      dispatchBulkConvertInProgressIfCourse(true);
      updateScopeBulkConvertInProgressState();
      applyBulkConvertSyncingStatus(convertibleLecturePageIds);

      dispatch(addAlertMessage({
        type: AlertMessageType.INFO,
        header: t.TIMELINE.LL_BULK_CONVERT_LECTURE_PAGES.ALERT_MESSAGES.PROCESSING(),
        message: t.TIMELINE.LL_BULK_CONVERT_LECTURE_PAGES.ALERT_MESSAGES.PROCESSING_MESSAGE({ ...aliases.lectureAliases, ...aliases.courseAliases }),
      }));
      handleClose();
    } else {
      dispatchBulkConvertInProgressIfCourse(false);
      dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.ERROR_SOMETHING_WRONG(),
      }));
    }
  }, [dispatch, scopeType, sourceId, dispatchBulkConvertInProgressIfCourse, updateScopeBulkConvertInProgressState, applyBulkConvertSyncingStatus, aliases.lectureAliases, aliases.courseAliases, handleClose]);

  const submitAction = useCallback(() => {
    const destinationCollectionId = getValues('destinationCollectionId');

    dispatch(bulkConvertToCollectionLessons({
      courseId: currentCourse.id,
      scopeType: getScopeType(sourceType),
      scopeId: sourceId,
      contentManagementCollectionId: destinationCollectionId,
    })).then(handleBulkConvertResponse);
  }, [getValues, dispatch, currentCourse.id, sourceType, sourceId, handleBulkConvertResponse]);

  return (
    <React.Fragment>
      <Global
        styles={styles}
      />
      <NvModal
        show={show}
        width={800}
        onClose={handleClose}
        type={ModalType.DYNAMIC}
        header={t.TIMELINE.LESSON_OPTIONS.ADD_TO_COLLECTION()}
        bodyClassName='p-0 overflow-clip'
        fullHeight={false}
        headerBgColor={gray7}
        body={(
          <div css={styles} className='mx-8 mt-6'>
            <SourceDetails sourceType={sourceType} sourceName={sourceName} pageCount={pageCount} />

            {mode === 'select' && (
              <React.Fragment>
                {canCreateNewCollection && (
                  <ClickableContainer
                    className='align-items-center d-flex mb-4'
                    onClick={() => setMode('create')}
                    aria-label={t.TIMELINE.LL_BULK_CONVERT_LECTURE_PAGES.LL_BULK_CONVERT_LECTURE_PAGES_MODAL.CREATE_NEW_COLLECTION()}
                  >
                    <NvIcon icon='add' size='small' className='text-primary' />
                    <div className='text-medium text-primary ml-2'>
                      {t.TIMELINE.LL_BULK_CONVERT_LECTURE_PAGES.LL_BULK_CONVERT_LECTURE_PAGES_MODAL.CREATE_NEW_COLLECTION()}
                    </div>
                  </ClickableContainer>
                )}

                <FormProvider {...methods}>
                  <form>
                    <CollectionListModalBody
                      className='collection-list-modal-body'
                      bodyDescription={t.TIMELINE.LL_BULK_CONVERT_LECTURE_PAGES.LL_BULK_CONVERT_LECTURE_PAGES_MODAL.SELECT_COLLECTION_DESCRIPTION(aliases.lectureAliases)}
                      collectionListModalType={CollectionListModalType.BULK_LINK}
                      modalActionType={ModalActionType.LINK}
                      submitAction={submitAction}
                      onClose={handleClose}
                    />
                  </form>
                </FormProvider>
              </React.Fragment>
            )}

            {mode === 'create' && canCreateNewCollection && (
              <React.Fragment>
                <CreateCollectionAndAddToCollection
                  onCancel={handleClose}
                  onBackToSelect={() => setMode('select')}
                  onSuccess={async (collectionId) => {
                    const result = await dispatch(bulkConvertToCollectionLessons({
                      courseId: currentCourse.id,
                      scopeType: getScopeType(sourceType),
                      scopeId: sourceId,
                      contentManagementCollectionId: collectionId,
                    }));

                    handleBulkConvertResponse(result);
                  }}
                />
              </React.Fragment>
            )}
          </div>
        )}
      />
    </React.Fragment>
  );
};

export default BulkAddToCollectionModal;
